import { ArrowRight, Briefcase, ChevronRight, ClipboardList, ExternalLink, Package, Receipt, RefreshCw, AlertOctagon, Ticket } from "lucide-react";

import { Footer } from "@/components/Frame";
import { money } from "@/lib/validators";
import { CURRENT_BRAND } from "@/brand/config";
import { detectCredential, isPartialCredential, KIND_META } from "@/lib/credential";

type Totals = { subtotal: string; tax: string; total: string };
type Item = {
  id?: string;
  sku: string;
  title?: string;
  meta?: string;
  lineTotal: string;
  icon?: string;
};

const ICONS: Record<string, typeof Briefcase> = {
  briefcase: Briefcase,
  receipt: Receipt,
};

export function CartReview({
  loading,
  error,
  empty,
  items,
  totals,
  codeOpen,
  code,
  setCode,
  codeErr,
  onToggleCode,
  onValidateCode,
  onRetry,
  onContinue,
  validating,
}: {
  loading: boolean;
  error: boolean;
  empty: boolean;
  items: Item[];
  totals: Totals;
  codeOpen: boolean;
  code: string;
  setCode: (v: string) => void;
  codeErr: string;
  onToggleCode: () => void;
  onValidateCode: () => void;
  onRetry: () => void;
  onContinue: () => void;
  validating: boolean;
}) {
  const marketingUrl = CURRENT_BRAND.branding?.marketingUrl || "https://www.synapta.mx";

  // ADR-CMRC-013 (Fase 3): un solo input acepta código de activación (UUID),
  // folio fiscal CFDI (UUID) o folio de cotización (SER-123 / numérico).
  // Detección client-side solo para UX; el backend resuelve de verdad.
  const kind = detectCredential(code);
  const partial = isPartialCredential(code);
  const kindMeta = kind !== "unknown" ? KIND_META[kind] : null;
  const formatErr =
    code.trim().length >= 4 && kind === "unknown" && !partial
      ? "Formato no reconocido. Acepta: código de activación, folio fiscal (UUID) o cotización (ej. RV-1024)."
      : "";

  const KindIcon =
    kindMeta?.icon === "clipboard" ? ClipboardList : kindMeta?.icon === "receipt" ? Receipt : Ticket;

  const codePanel = (
    <div className="code-panel">
      <div className="code-panel__h">
        <Ticket size={16} strokeWidth={1.6} /> Código, factura o cotización
      </div>
      <input
        className={"code-input" + (codeErr || formatErr ? " code-input--err" : "")}
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Código UUID · folio fiscal CFDI · cotización RV-1024"
        maxLength={64}
        aria-label="Código de activación, folio fiscal CFDI o folio de cotización"
        autoComplete="off"
        spellCheck={false}
      />
      {kindMeta && !codeErr && (
        <div
          className="code-card__ok"
          style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}
          aria-live="polite"
        >
          <KindIcon size={14} strokeWidth={1.8} /> {kindMeta.sub}
        </div>
      )}
      {(codeErr || formatErr) && (
        <div className="fld__err" style={{ marginTop: 8 }}>
          <AlertOctagon size={14} strokeWidth={1.8} />
          {codeErr || formatErr}
        </div>
      )}
      <button
        className="btn btn--primary btn--block"
        style={{ marginTop: 12 }}
        disabled={kind === "unknown" || validating}
        onClick={onValidateCode}
      >
        {validating ? (
          <>
            <RefreshCw size={18} className="spin" /> Validando…
          </>
        ) : (
          "Validar"
        )}
      </button>
    </div>
  );

  if (empty) {
    return (
      <>
        <div className="screen content--center">
          <div className="empty">
            <div className="empty__ic">
              <Package size={40} strokeWidth={1.4} />
            </div>
            <div className="empty__title">No tienes nada en el carrito</div>
            <div className="empty__sub">
              ¿Tienes un código de invitación? Ingrésalo para activar tu cuenta.
            </div>
            {!codeOpen ? (
              <button
                className="btn btn--secondary btn--block"
                style={{ marginTop: 22 }}
                onClick={onToggleCode}
              >
                <Ticket size={18} strokeWidth={1.6} /> Ingresar código
              </button>
            ) : (
              codePanel
            )}
            <div className="empty__link">
              o explora los planes en{" "}
              <a href={marketingUrl} target="_blank" rel="noreferrer">
                {marketingUrl.replace(/^https?:\/\//, "")} <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
        <Footer>{null}</Footer>
      </>
    );
  }

  return (
    <>
      <div className="screen">
        <div className="eyebrow">Resumen</div>
        <h1 className="h1" style={{ marginTop: 6 }}>
          Tu pedido
        </h1>

        {error ? (
          <div style={{ marginTop: 20 }}>
            <div className="banner banner--warn">
              <AlertOctagon size={18} strokeWidth={1.7} />
              <span>No pudimos cargar tu cotización.</span>
            </div>
            <button
              className="btn btn--ghost btn--block"
              style={{ marginTop: 14 }}
              onClick={onRetry}
            >
              <RefreshCw size={18} /> Reintentar
            </button>
          </div>
        ) : loading ? (
          <div style={{ marginTop: 20 }}>
            <div className="sk sk-row"></div>
            <div className="sk sk-row"></div>
            <div
              className="sk"
              style={{ height: 120, borderRadius: 14, marginTop: 8 }}
            ></div>
          </div>
        ) : (
          <>
            <div className="stack" style={{ marginTop: 20 }}>
              {items.map((it) => {
                const Icon = ICONS[it.icon || ""] || Briefcase;
                return (
                  <button
                    key={it.id || it.sku}
                    className="plan-row"
                    type="button"
                  >
                    <span className="plan-row__ic">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <span className="plan-row__body">
                      <span className="plan-row__title">
                        {it.title || it.sku}
                      </span>
                      {it.meta && (
                        <span className="plan-row__meta">{it.meta}</span>
                      )}
                    </span>
                    <span className="plan-row__price tnum">
                      {money(it.lineTotal)}
                    </span>
                    <ChevronRight size={18} className="plan-row__chev" />
                  </button>
                );
              })}
            </div>

            <div className="totals">
              <div className="trow">
                <span className="trow__k">Subtotal</span>
                <span className="trow__v tnum">{money(totals.subtotal)}</span>
              </div>
              <div className="trow">
                <span className="trow__k">IVA (16%)</span>
                <span className="trow__v tnum">{money(totals.tax)}</span>
              </div>
              <div className="trow trow--total">
                <span className="trow__k">Total</span>
                <span className="trow__v tnum">{money(totals.total)}</span>
              </div>
            </div>

            {!codeOpen ? (
              <button
                className="footer__link"
                style={{
                  marginTop: 18,
                  alignSelf: "flex-start",
                  paddingLeft: 2,
                }}
                onClick={onToggleCode}
              >
                ¿Tienes un código? →
              </button>
            ) : (
              codePanel
            )}
          </>
        )}
      </div>
      <Footer>
        <button
          className="btn btn--primary btn--block"
          disabled={loading || error}
          onClick={onContinue}
        >
          Continuar <ArrowRight size={20} />
        </button>
      </Footer>
    </>
  );
}
